import { useCallback, useEffect, useRef, useState, type RefObject } from "react"

export interface UseMapInteractionTrackerOptions {
  /** Null while mounting. */
  map: google.maps.Map | null
  /** Drag/zoom events while true count as programmatic. */
  ignoringBoundsChangesRef?: RefObject<boolean>
  /** Drag/zoom events before true are typically mount-time, not user input. */
  initialBoundsSetRef?: RefObject<boolean>
}

export interface UseMapInteractionTrackerResult {
  userHasInteracted: boolean
  userHasInteractedRef: RefObject<boolean>
  /** Mark interaction manually (e.g. from an external onWheel handler). */
  markUserInteraction: () => void
  resetUserInteraction: () => void
}

/**
 * Tracks user-driven map drag/zoom. Pair with useMapAutoFit so events
 * from programmatic fits don't count as user interaction.
 */
export function useMapInteractionTracker({
  map,
  ignoringBoundsChangesRef,
  initialBoundsSetRef,
}: UseMapInteractionTrackerOptions): UseMapInteractionTrackerResult {
  const [userHasInteracted, setUserHasInteracted] = useState(false)
  const userHasInteractedRef = useRef(false)
  userHasInteractedRef.current = userHasInteracted

  const markUserInteraction = useCallback(() => {
    setUserHasInteracted(true)
  }, [])

  const resetUserInteraction = useCallback(() => {
    setUserHasInteracted(false)
  }, [])

  useEffect(() => {
    if (!map) return

    const dragStart = map.addListener("dragstart", () => {
      if (initialBoundsSetRef?.current ?? true) {
        setUserHasInteracted(true)
      }
    })

    const zoomChanged = map.addListener("zoom_changed", () => {
      const initial = initialBoundsSetRef?.current ?? true
      const ignoring = ignoringBoundsChangesRef?.current ?? false
      if (initial && !ignoring) {
        setUserHasInteracted(true)
      }
    })

    return () => {
      dragStart.remove()
      zoomChanged.remove()
    }
  }, [map, ignoringBoundsChangesRef, initialBoundsSetRef])

  return {
    userHasInteracted,
    userHasInteractedRef,
    markUserInteraction,
    resetUserInteraction,
  }
}
